import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { baseUrl, endpoints } from '../../api/api';
import { AdminBook, Version, getJourneyType } from './types';
import AdminHeader from './components/AdminHeader';
import BookInfoSection from './components/BookInfoSection';
import VersionsTable from './components/VersionsTable';
import RejectModal from './components/RejectModal';
import IsbnSection from './components/IsbnSection';
import { useAlert } from '../../components/AlertBanner';

const BookDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [book, setBook] = useState<AdminBook | null>(null);
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<Version | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isResettingQuota, setIsResettingQuota] = useState(false);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string | null>(null);
  const [isLoadingManuscriptPreview, setIsLoadingManuscriptPreview] = useState(false);

  const fetchBookDetails = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const url = `${baseUrl}${endpoints.getAdminBookDetail.replace(':id', id!)}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBook(response.data.book);
      setVersions(response.data.versions);
    } catch (error) {
      console.error('Failed to fetch book details:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        if (userData.role !== 'admin') {
          navigate('/');
          return;
        }
      } catch {
        navigate('/');
        return;
      }
    }
    fetchBookDetails();
  }, [fetchBookDetails, navigate]);

  useEffect(() => {
    if (!book?.coverImage) return;
    const filename = typeof book.coverImage === 'string'
      ? book.coverImage
      : (book.coverImage as any)?.filename;
    if (!filename) return;

    let objectUrl: string;
    const token = localStorage.getItem('accessToken');
    axios.get(
      `${baseUrl}${endpoints.downloadFileByFilename.replace(':filename', encodeURIComponent(filename))}`,
      { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } }
    ).then(res => {
      objectUrl = URL.createObjectURL(res.data);
      setCoverPreviewUrl(objectUrl);
    }).catch(() => {});

    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [book?.coverImage]);

  const handleApproveVersion = async (versionId: string) => {
    setIsProcessing(true);
    try {
      const token = localStorage.getItem('accessToken');
      const url = `${baseUrl}${endpoints.reviewVersion
        .replace(':id', id!)
        .replace(':versionId', versionId)}`;
      await axios.put(url, { status: 'approved' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchBookDetails();
    } catch (error) {
      showAlert('Failed to approve version', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectClick = (version: Version) => {
    setSelectedVersion(version);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim() || !selectedVersion) return;
    setIsProcessing(true);
    try {
      const token = localStorage.getItem('accessToken');
      const url = `${baseUrl}${endpoints.reviewVersion
        .replace(':id', id!)
        .replace(':versionId', selectedVersion._id)}`;
      await axios.put(url, {
        status: 'rejected',
        comment: rejectionReason
      }, { headers: { Authorization: `Bearer ${token}` } });
      setShowRejectModal(false);
      setSelectedVersion(null);
      setRejectionReason('');
      await fetchBookDetails();
    } catch (error) {
      showAlert('Failed to reject version', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadVersionPDF = async (version: Version) => {
    try {
      const token = localStorage.getItem('accessToken');
      const url = `${baseUrl}${endpoints.downloadVersionPDF
        .replace(':id', id!)
        .replace(':versionId', version._id)}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${book?.title || 'book'}-v${version.versionNumber}.pdf`;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      showAlert('Failed to download PDF', 'error');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleDownloadCover = async () => {
    const filename = typeof book?.coverImage === 'string'
      ? book.coverImage
      : (book?.coverImage as any)?.filename;
    if (!filename) return;
    try {
      const token = localStorage.getItem('accessToken');
      const url = `${baseUrl}${endpoints.downloadFileByFilename.replace(':filename', encodeURIComponent(filename))}`;
      const response = await axios.get(url, { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } });
      const downloadUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch {
      showAlert('Failed to download cover image', 'error');
    }
  };

  // Preview whatever the author currently has — before any submission ever
  // happens. Uploaded-manuscript books: just open the raw file (the generic
  // download endpoint already permits admins to fetch any user's file).
  // Typed-in-editor books: generate a real preview PDF from the CURRENT
  // sections via the same stateless endpoint the editor's own "Generate
  // Preview" button calls — nothing gets persisted or overwritten, this is
  // read-only.
  const handlePreviewManuscript = async () => {
    if (!book) return;
    const token = localStorage.getItem('accessToken');

    if (book.uploadedFile?.filename) {
      try {
        const url = `${baseUrl}${endpoints.downloadFileByFilename.replace(':filename', encodeURIComponent(book.uploadedFile.filename))}`;
        const response = await axios.get(url, { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } });
        const blobUrl = window.URL.createObjectURL(response.data);
        window.open(blobUrl, '_blank');
      } catch {
        showAlert('Failed to load uploaded manuscript', 'error');
      }
      return;
    }

    const sections = book.content?.json?.sections;
    const nonEmptySections = (sections || []).filter(
      (s: any) => s?.content && s.content.trim() !== '' && s.content !== '<p></p>'
    );
    if (nonEmptySections.length === 0) {
      showAlert('This book has no uploaded file or written content yet.', 'info');
      return;
    }

    setIsLoadingManuscriptPreview(true);
    try {
      const response = await axios.post(`${baseUrl}${endpoints.previewPDF}`, {
        sections: nonEmptySections.map((s: any) => ({ id: s.id, title: s.title, content: s.content, type: s.type || 'chapter' })),
        bookTitle: book.title || 'Book',
        bookSize: book.bookSize,
        language: book.language,
        bookMetadata: { author: book.author, subtitle: book.subtitle },
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (response.data.success && response.data.pdf) {
        const cleanBase64 = response.data.pdf.replace(/\s/g, '');
        const binaryString = await fetch(`data:application/pdf;base64,${cleanBase64}`);
        const pdfBlob = await binaryString.blob();
        const blobUrl = URL.createObjectURL(pdfBlob);
        window.open(blobUrl, '_blank');
      } else {
        showAlert('Failed to generate preview', 'error');
      }
    } catch {
      showAlert('Failed to generate preview', 'error');
    } finally {
      setIsLoadingManuscriptPreview(false);
    }
  };

  const handleResetGenerations = async () => {
    if (!window.confirm(`Reset AI cover generations for "${book?.title}"? The author will get a fresh quota.`)) return;
    setIsResettingQuota(true);
    try {
      const token = localStorage.getItem('accessToken');
      const url = `${baseUrl}${endpoints.adminResetBookGenerations.replace(':bookId', id!)}`;
      await axios.patch(url, {}, { headers: { Authorization: `Bearer ${token}` } });
      showAlert('AI cover generations reset successfully', 'success');
      await fetchBookDetails();
    } catch {
      showAlert('Failed to reset generations', 'error');
    } finally {
      setIsResettingQuota(false);
    }
  };

  const handleBack = () => navigate('/admin');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="min-h-screen bg-gray-100">
        <AdminHeader onLogout={handleLogout} onBack={handleBack} title="Book Details" />
        <main className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center text-gray-500">Book not found</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminHeader onLogout={handleLogout} onBack={handleBack} title="Book Details" />
      <main className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        <BookInfoSection book={book} />
        <IsbnSection book={book} onUpdated={fetchBookDetails} />

        {/* Manuscript preview — the author's current draft, whether or not
            they've ever submitted. Only ever reads existing data; generating
            a preview for typed content doesn't persist or overwrite anything. */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Manuscript</h2>
          </div>
          <div className="px-6 py-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Journey</p>
              <p className="text-sm font-medium text-gray-900">{getJourneyType(book)}</p>
            </div>
            <button
              onClick={handlePreviewManuscript}
              disabled={isLoadingManuscriptPreview}
              className="bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
            >
              {isLoadingManuscriptPreview ? 'Generating…' : 'Preview Current Draft'}
            </button>
          </div>
        </div>

        {/* AI Cover Generation */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">AI Cover Generation</h2>
          </div>
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Generations used</p>
                <p className="text-2xl font-bold text-gray-900">{book.aiCoverGenerations ?? 0}</p>
              </div>
              <div className="text-gray-300 text-xl">/</div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Limit</p>
                <p className="text-2xl font-bold text-gray-900">{process.env.REACT_APP_AI_COVER_LIMIT || '5'}</p>
              </div>
            </div>
            <button
              onClick={handleResetGenerations}
              disabled={isResettingQuota || (book.aiCoverGenerations ?? 0) === 0}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border transition-colors disabled:opacity-40 disabled:cursor-not-allowed border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100"
            >
              {isResettingQuota ? (
                <span className="inline-block w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
              Reset Usage
            </button>
          </div>
        </div>

        {/* Cover Image */}
        {book.coverImage && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Cover Image</h2>
              <button
                onClick={handleDownloadCover}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-600 hover:text-orange-700 border border-orange-200 bg-orange-50 hover:bg-orange-100 px-3 py-1.5 rounded-md transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Cover
              </button>
            </div>
            <div className="px-6 py-4 flex items-start gap-6">
              {coverPreviewUrl ? (
                <img
                  src={coverPreviewUrl}
                  alt="Book cover"
                  className="h-48 w-auto rounded shadow-md object-contain border border-gray-200"
                />
              ) : (
                <div className="h-48 w-32 rounded border border-gray-200 bg-gray-50 flex items-center justify-center text-gray-400 text-xs">
                  Loading…
                </div>
              )}
              <div className="pt-1">
                <p className="text-xs text-gray-400 font-mono break-all">
                  {typeof book.coverImage === 'string' ? book.coverImage : (book.coverImage as any)?.filename}
                </p>
              </div>
            </div>
          </div>
        )}

        <VersionsTable
          versions={versions}
          isProcessing={isProcessing}
          onDownloadPDF={handleDownloadVersionPDF}
          onApprove={handleApproveVersion}
          onReject={handleRejectClick}
        />
      </main>
      {showRejectModal && selectedVersion && (
        <RejectModal
          itemTitle={`Version ${selectedVersion.versionNumber}`}
          rejectionReason={rejectionReason}
          isProcessing={isProcessing}
          onReasonChange={setRejectionReason}
          onConfirm={handleRejectConfirm}
          onClose={() => setShowRejectModal(false)}
        />
      )}
    </div>
  );
};

export default BookDetails;
